import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
  SimpleGrid,
  Box,
  Divider,
  InputGroup,
  InputRightElement,
  HStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { Dictamen } from './types';
import ModalInspeccion from './ModalInspeccion';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';
import { MdAssignmentTurnedIn } from 'react-icons/md';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';

const tiposDictamen = [
  { value: 'seguridad', label: 'Dictamen de Seguridad y Prevención' },
  { value: 'riesgo', label: 'Dictamen de Riesgo' },
  { value: 'habitabilidad', label: 'Dictamen de Habitabilidad' },
];




const estatusEntrega = ['Entregado', 'Pendiente de entrega'];
const estatusPago = ['Pagado', 'No pagado'];

const estatusDictamen = ['Pendiente', 'En revisión', 'Aprobado', 'Rechazado'];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onGuardar: (d: Dictamen) => void;
  dictamen?: Dictamen | null;
}

export default function ModalDictamen({ isOpen, onClose, onGuardar, dictamen }: Props) {
  const toast = useToast();
  const [modalInspeccionOpen, setModalInspeccionOpen] = useState(false);
const [form, setForm] = useState<Partial<Dictamen>>({ 
    contenidoDictamen: '',
    estatusEntrega: 'Pendiente de entrega',
    estatusPago: 'No pagado',
    direccion: 'San Juan Totolac, Tlax., México',
    latitud: 19.3256653,
    longitud: -98.2568412
  });
  const [catalogos, setCatalogos] = useState<{ usoInmueble: any[], tipoInmueble: any[] }>({ usoInmueble: [], tipoInmueble: [] });
  const [tipoDictamen, setTipoDictamen] = useState<string>('');

  useEffect(() => {
    if (dictamen) {
      // Cargar datos del dictamen existente
      const formData = { ...dictamen };
      
      // Cargar datos específicos según el tipo
      if (dictamen.dictamenRiesgo) {
        formData.usoInmuebleId = dictamen.dictamenRiesgo.usoInmuebleId;
        formData.superficie = dictamen.dictamenRiesgo.superficie;
        formData.caracteristicas = dictamen.dictamenRiesgo.caracteristicas;
        formData.tipoActEven = dictamen.dictamenRiesgo.tipoActEven;
        formData.aforo = dictamen.dictamenRiesgo.aforo;
        formData.medidasMinimasTexto = dictamen.dictamenRiesgo.medidasMinimas;
        
        // Establecer categoría si hay uso de inmueble
        if ((dictamen.dictamenRiesgo as any).usoInmueble) {
          (formData as any).categoriaUso = (dictamen.dictamenRiesgo as any).usoInmueble.categoria;
        }
      }
      
      if (dictamen.dictamenHabitabilidad) {
        formData.referenciaInmueble = dictamen.dictamenHabitabilidad.referenciaInmueble;
        formData.tipoInmuebleId = dictamen.dictamenHabitabilidad.tipoInmuebleId;
        formData.superficeConstruida = dictamen.dictamenHabitabilidad.superficeConstruida;
        formData.niveles = dictamen.dictamenHabitabilidad.niveles;
        formData.predioDenominacion = dictamen.dictamenHabitabilidad.predioDenominacion;
      }
      
      if (dictamen.dictamenSeguridad) {
        formData.giro = dictamen.dictamenSeguridad.giro;
        formData.denominacionNegocio = dictamen.dictamenSeguridad.denominacionNegocio;
        formData.medidasMinimas = dictamen.dictamenSeguridad.medidasMinimas;
      }
      
      setForm(formData);
      
      // Determinar tipo de dictamen
      if (dictamen?.tipo) {
        const tipoEncontrado = tiposDictamen.find(t => dictamen.tipo?.includes(t.label));
        setTipoDictamen(tipoEncontrado?.value || '');
      }
    } else {
      // Nuevo dictamen - valores por defecto
      setForm({ 
        contenidoDictamen: '',
        estatusEntrega: 'Pendiente de entrega',
        estatusPago: 'No pagado',
        direccion: 'San Juan Totolac, Tlax., México',
        latitud: 19.3256653,
        longitud: -98.2568412
      });
    }
  }, [dictamen]);

  useEffect(() => {
    const fetchCatalogos = async () => {
      try {
        const [usoRes, tipoRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/catalogos/uso-inmueble`),
          fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/catalogos/tipo-inmueble`)
        ]);
        
        const [usoData, tipoData] = await Promise.all([
          usoRes.json(),
          tipoRes.json()
        ]);
        
        setCatalogos({
          usoInmueble: usoData.success ? usoData.data : [],
          tipoInmueble: tipoData.success ? tipoData.data : []
        });
      } catch (error) {
        console.error('Error loading catalogs:', error);
      }
    };
    
    const initMap = () => {
      const center = {
        lat: form.latitud ? parseFloat(form.latitud.toString()) : 19.3256653,
        lng: form.longitud ? parseFloat(form.longitud.toString()) : -98.2568412
      };

      const map = new (window as any).google.maps.Map(document.getElementById('mapa-dictamen'), {
        zoom: 15,
        center: center,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true
      });

      let marker: any = null;

      // Si ya hay coordenadas, mostrar marcador
      if (form.latitud && form.longitud) {
        marker = new (window as any).google.maps.Marker({
          position: center,
          map: map,
          draggable: true
        });

        marker.addListener('dragend', (event: any) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          
          const geocoder = new (window as any).google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              setForm(prev => ({
                ...prev,
                latitud: lat,
                longitud: lng,
                direccion: results[0].formatted_address
              }));
            } else {
              setForm(prev => ({ ...prev, latitud: lat, longitud: lng }));
            }
          });
        });
      }

      // Agregar listener para clics en el mapa
      map.addListener('click', (event: any) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        // Remover marcador anterior si existe
        if (marker) {
          marker.setMap(null);
        }

        // Crear nuevo marcador
        marker = new (window as any).google.maps.Marker({
          position: { lat, lng },
          map: map,
          draggable: true
        });

        marker.addListener('dragend', (dragEvent: any) => {
          const newLat = dragEvent.latLng.lat();
          const newLng = dragEvent.latLng.lng();
          
          const geocoder = new (window as any).google.maps.Geocoder();
          geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              setForm(prev => ({
                ...prev,
                latitud: newLat,
                longitud: newLng,
                direccion: results[0].formatted_address
              }));
            } else {
              setForm(prev => ({ ...prev, latitud: newLat, longitud: newLng }));
            }
          });
        });

        const geocoder = new (window as any).google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
          if (status === 'OK' && results[0]) {
            setForm(prev => ({
              ...prev,
              latitud: lat,
              longitud: lng,
              direccion: results[0].formatted_address
            }));
          } else {
            setForm(prev => ({ ...prev, latitud: lat, longitud: lng }));
          }
        });
      });
    };
    
    if (isOpen) {
      fetchCatalogos();
      
      // Inicializar mapa
      setTimeout(() => {
        if ((window as any).google && (window as any).google.maps) {
          initMap();
        } else {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&callback=initMapDictamen`;
          script.async = true;
          (window as any).initMapDictamen = initMap;
          document.head.appendChild(script);
        }
      }, 100);
    }
  }, [isOpen, form.latitud, form.longitud]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, files } = e.target as any;
    if (files) {
      setForm(prev => ({ ...prev, [name]: files[0] }));
    } else {
      if (name === 'tipoDictamen') {
        setTipoDictamen(value);
        const tipoLabel = tiposDictamen.find(t => t.value === value)?.label || '';
        setForm(prev => ({ ...prev, tipo: tipoLabel }));
      } else {
        setForm(prev => ({ ...prev, [name]: value }));
      }
    }
  };

  const camposRequeridos = ['tipo', 'nombre', 'negocio', 'direccion', ];

  const camposArchivo = [
    { name: 'planos', label: 'Planos del establecimiento' },
    { name: 'contratoGas', label: 'Contrato de uso de gas' },
    { name: 'memoriaSeguridad', label: 'Memoria descriptiva' },
    { name: 'documentacionLegal', label: 'Documentación legal del solicitante' },
  ];

 const handleGuardar = async () => {
  // Validar tipo de dictamen
  if (!tipoDictamen) {
    toast({
      title: 'Tipo de dictamen requerido',
      description: 'Selecciona el tipo de dictamen',
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
    return;
  }

  // Validar campos requeridos
  if (!form.nombre || !form.direccion || !form.colonia) {
    toast({
      title: 'Campos requeridos',
      description: 'Complete todos los campos obligatorios',
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
    return;
  }

  const dataAEnviar = {
    ...form,
    tipo: tiposDictamen.find(t => t.value === tipoDictamen)?.label || '',
    contenidoDictamen: form.contenidoDictamen || '',
    montoPago: form.montoPago ? parseFloat(form.montoPago.toString()) : 0,
    creadoPor: 'Usuario',
  };

  try {
    let url: string;
    
    if (dictamen) {
      // Editar dictamen existente
      url = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/dictamenes/${dictamen.id}`;
    } else {
      // Crear nuevo dictamen según el tipo
      url = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/dictamenes/${tipoDictamen}`;
    }

    const metodo = dictamen ? 'PUT' : 'POST';

    const respuesta = await fetch(url, {
      method: metodo,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataAEnviar),
    });

    if (!respuesta.ok) {
      const errorText = await respuesta.text();
      throw new Error(`Error ${respuesta.status}: ${errorText}`);
    }

    const resultado = await respuesta.json();

    if (!resultado.success) {
      throw new Error(resultado.error || resultado.message || 'Error al guardar dictamen');
    }

    toast({
      title: dictamen ? 'Dictamen actualizado' : 'Dictamen creado',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    onGuardar(resultado.data);
    setForm({ contenidoDictamen: '' });
    setTipoDictamen('');
    onClose();
  } catch (error) {
    console.error('Error al crear dictamen de seguridad:', error);
    
    let errorMessage = 'Error desconocido al guardar el dictamen';
    
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    toast({
      title: 'Error al crear dictamen de seguridad',
      description: errorMessage,
      status: 'error',
      duration: 4000,
      isClosable: true,
    });
  }
};


  const handleQuillChange = (value: string) => {
  setForm(prev => ({ ...prev, contenidoDictamen: value }));
};

  const buscarDireccion = (direccion: string) => {
    if (!direccion) return;
    
    const geocoder = new (window as any).google.maps.Geocoder();
    geocoder.geocode({ address: direccion }, (results: any, status: any) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setForm(prev => ({
          ...prev,
          latitud: loc.lat(),
          longitud: loc.lng(),
          direccion: results[0].formatted_address
        }));
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Especifica una dirección más precisa.',
          status: 'error',
        });
      }
    });
  };

  const obtenerUbicacionActual = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          const geocoder = new (window as any).google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              setForm(prev => ({
                ...prev,
                latitud: lat,
                longitud: lng,
                direccion: results[0].formatted_address
              }));
            } else {
              setForm(prev => ({ ...prev, latitud: lat, longitud: lng }));
            }
          });
        },
        (error) => {
          toast({
            title: 'Error de geolocalización',
            description: 'No se pudo obtener la ubicación actual.',
            status: 'error',
          });
        }
      );
    } else {
      toast({
        title: 'Geolocalización no disponible',
        description: 'Tu navegador no soporta geolocalización.',
        status: 'error',
      });
    }
  };


  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          position="relative"
        >
          {dictamen ? 'Editar' : 'Nuevo'} Dictamen
        </ModalHeader>
        
        <ModalCloseButton 
          color="white" 
          _hover={{ bg: 'rgba(255,255,255,0.2)' }} 
          onClick={() => {
            setForm({ 
              contenidoDictamen: '',
              estatusEntrega: 'Pendiente de entrega',
              estatusPago: 'No pagado',
              direccion: 'San Juan Totolac, Tlax., México',
              latitud: 19.3256653,
              longitud: -98.2568412
            });
            setTipoDictamen('');
          }}
        />
      <ModalBody>
        {/* <Button
  leftIcon={<MdAssignmentTurnedIn />}
  colorScheme="purple"
  variant="outline"
  onClick={() => setModalInspeccionOpen(true)}
>
  Inspección
</Button> */}
{/* SECCIÓN: Información del Dictamen */}
<Box mt={6} mb={4}>
  <FormLabel fontSize="lg" fontWeight="bold">Información del Dictamen</FormLabel>
  <Divider mb={4} />

  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
    <FormControl isRequired>
      <FormLabel>Tipo de Dictamen</FormLabel>
      <Select 
        name="tipoDictamen" 
        value={tipoDictamen} 
        onChange={handleChange}
        isDisabled={!!dictamen}
      >
        <option value="">Seleccione...</option>
        {tiposDictamen.map(t => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </Select>
    </FormControl>

    <FormControl isRequired>
      <FormLabel>Operador Responsable</FormLabel>
      <Input name="operador" value={form.operador || ''} onChange={handleChange} placeholder="Ingrese el nombre del operador" />
    </FormControl>
  </SimpleGrid>
</Box>
 <FormControl isRequired>
    <FormLabel>Nombre del Propietario </FormLabel>
    <Input name="nombre" value={form.nombre || ''} onChange={handleChange} />
  </FormControl>
 



{/* 
  <FormControl isRequired>
    <FormLabel>Municipio</FormLabel>
    <Input name="municipio" value={form.municipio || ''} onChange={handleChange} />
  </FormControl>

  <FormControl isRequired>
    <FormLabel>Estado</FormLabel>
    <Input name="estado" value={form.estado || ''} onChange={handleChange} />
  </FormControl> */}

  {/* <FormControl>
    <FormLabel>Nomenclatura</FormLabel>
    <Input name="nomenclatura" value={form.nomenclatura || ''} onChange={handleChange} />
  </FormControl> */}

  
 


  {/* <FormControl>
    <FormLabel>Observaciones</FormLabel>
    <Textarea name="observaciones" value={form.observaciones || ''} onChange={handleChange} />
  </FormControl> */}
{/* 
  {camposArchivo.map(campo => (
    <FormControl key={campo.name}>
      <FormLabel>{campo.label}</FormLabel>
      <Input type="file" name={campo.name} onChange={handleChange} />
    </FormControl>
  ))} */}
{/* SECCIÓN: Información del Negocio */}
<Box mt={6} mb={4}>
  {/*<FormLabel fontSize="lg" fontWeight="bold">Información del Negocio</FormLabel>*/}
  <Divider mb={4} />

  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
    <FormControl isRequired>
      <FormLabel>Domicilio</FormLabel>
      <Input name="direccion" value={form.direccion || ''} onChange={handleChange} />
    </FormControl>

    <FormControl isRequired>
      <FormLabel>Colonia / Localidad</FormLabel>
      <Input name="colonia" value={form.colonia || ''} onChange={handleChange} />
    </FormControl>
  </SimpleGrid>
  
  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
    <FormControl>
      <FormLabel>Latitud</FormLabel>
      <Input 
        type="number" 
        step="0.000001" 
        name="latitud" 
        value={form.latitud || ''} 
        onChange={handleChange} 
        placeholder="Ej: 19.432608"
      />
    </FormControl>
    
    <FormControl>
      <FormLabel>Longitud</FormLabel>
      <Input 
        type="number" 
        step="0.000001" 
        name="longitud" 
        value={form.longitud || ''} 
        onChange={handleChange} 
        placeholder="Ej: -99.133209"
      />
    </FormControl>
  </SimpleGrid>
  
  <Box mt={4}>
    <FormLabel>Domicilio</FormLabel>
    <InputGroup mb={3}>
      <Input
        value={form.direccion || ''}
        onChange={handleChange}
        name="direccion"
        placeholder="Ingresa la dirección"
      />
      <InputRightElement width="9rem" mr={10}>
        <HStack spacing={1}>
          <Button h="1.75rem" size="sm" onClick={obtenerUbicacionActual}>
            Ubicación Actual
          </Button>
          <Button h="1.75rem" size="sm" onClick={() => buscarDireccion(form.direccion || '')}>
            Buscar
          </Button>
        </HStack>
      </InputRightElement>
    </InputGroup>
    
    <Box 
      id="mapa-dictamen" 
      height="200px" 
      width="100%" 
      border="1px solid #e2e8f0" 
      borderRadius="md"
    >
      {/* El mapa se inicializará aquí */}
    </Box>
    <Box fontSize="sm" color="gray.600" mt={2}>
      Haz clic en el mapa para seleccionar la ubicación del dictamen
    </Box>
  </Box>
</Box>

{/* CAMPOS ESPECÍFICOS POR TIPO DE DICTAMEN */}
{tipoDictamen === 'seguridad' && (
  <Box mt={6} mb={4}>
    <FormLabel fontSize="lg" fontWeight="bold">Información de Seguridad</FormLabel>
    <Divider mb={4} />
    
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      <FormControl>
        <FormLabel>Giro Comercial</FormLabel>
        <Input name="giro" value={form.giro || ''} onChange={handleChange} />
      </FormControl>
      
      <FormControl>
        <FormLabel>Denominación del Negocio</FormLabel>
        <Input name="denominacionNegocio" value={form.denominacionNegocio || ''} onChange={handleChange} />
      </FormControl>
      
      <FormControl>
        <FormLabel>¿Cuenta con medidas mínimas?</FormLabel>
        <Select name="medidasMinimas" value={form.medidasMinimas?.toString() || ''} onChange={handleChange}>
          <option value="">Seleccione...</option>
          <option value="true">Sí</option>
          <option value="false">No</option>
        </Select>
      </FormControl>
    </SimpleGrid>
  </Box>
)}

{tipoDictamen === 'riesgo' && (
  <Box mt={6} mb={4}>
    <FormLabel fontSize="lg" fontWeight="bold">Información de Riesgo</FormLabel>
    <Divider mb={4} />
    
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      <FormControl>
        <FormLabel>Categoría de Uso</FormLabel>
        <Select name="categoriaUso" value={(form as any).categoriaUso || ''} onChange={handleChange}>
          <option value="">Seleccione categoría...</option>
          <option value="HABITACIONAL">Habitacional</option>
          <option value="COMERCIAL">Comercial</option>
          <option value="INDUSTRIAL">Industrial</option>
          <option value="OFICINA">Oficina</option>
          <option value="EDUCATIVO">Educativo</option>
          <option value="SALUD">Salud</option>
          <option value="EVENTUAL">Eventual</option>
          <option value="TURISTICO">Turístico</option>
          <option value="DEPORTIVO_CULTURAL">Deportivo/Cultural</option>
          <option value="OTRO">Otro</option>
        </Select>
      </FormControl>
      
      {(form as any).categoriaUso && (
        <FormControl>
          <FormLabel>Uso del Inmueble</FormLabel>
          <Select name="usoInmuebleId" value={form.usoInmuebleId?.toString() || ''} onChange={handleChange}>
            <option value="">Seleccione uso...</option>
            {catalogos.usoInmueble
              .filter(uso => uso.categoria === (form as any).categoriaUso)
              .map(uso => (
                <option key={uso.id} value={uso.id}>{uso.descripcion}</option>
              ))}
          </Select>
          {form.usoInmuebleId?.toString() === catalogos.usoInmueble.find(u => u.codigo === 'OTR001')?.id?.toString() && (
            <Input 
              mt={2}
              name="usoInmueblePersonalizado" 
              value={(form as any).usoInmueblePersonalizado || ''} 
              onChange={handleChange}
              placeholder="Especifique el uso del inmueble..."
            />
          )}
        </FormControl>
      )}
      
      <FormControl>
        <FormLabel>Superficie (m²)</FormLabel>
        <Input type="number" name="superficie" value={form.superficie?.toString() || ''} onChange={handleChange} />
      </FormControl>

      <FormControl>
        <FormLabel>Características del Inmueble</FormLabel>
        <Textarea name="caracteristicas" value={form.caracteristicas || ''} onChange={handleChange} />
      </FormControl>
      
      <FormControl>
        <FormLabel>Tipo de Actividad/Evento</FormLabel>
        <Input name="tipoActEven" value={form.tipoActEven || ''} onChange={handleChange} />
      </FormControl>
      
      <FormControl>
        <FormLabel>Aforo Permitido</FormLabel>
        <Input type="number" name="aforo" value={form.aforo?.toString() || ''} onChange={handleChange} />
      </FormControl>
    </SimpleGrid>
    
    
    
    <FormControl mt={4}>
      <FormLabel>Medidas de Seguridad</FormLabel>
      <Textarea name="medidasMinimasTexto" value={form.medidasMinimasTexto || ''} onChange={handleChange} />
    </FormControl>
  </Box>
)}

{tipoDictamen === 'habitabilidad' && (
  <Box mt={6} mb={4}>
    <FormLabel fontSize="lg" fontWeight="bold">Información de Habitabilidad</FormLabel>
    <Divider mb={4} />
    
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>

      <FormControl mt={4}>
        <FormLabel>Referencias del Inmueble</FormLabel>
        <Textarea name="referenciaInmueble" value={form.referenciaInmueble || ''} onChange={handleChange} />
      </FormControl>

      <FormControl>
        <FormLabel>Tipo de Inmueble</FormLabel>
        <Select name="tipoInmuebleId" value={form.tipoInmuebleId?.toString() || ''} onChange={handleChange}>
          <option value="">Seleccione...</option>
          {catalogos.tipoInmueble.map(tipo => (
            <option key={tipo.id} value={tipo.id}>{tipo.descripcion}</option>
          ))}
        </Select>
        {form.tipoInmuebleId?.toString() === catalogos.tipoInmueble.find(t => t.codigo === 'OTR001')?.id?.toString() && (
          <Input 
            mt={2}
            name="tipoInmueblePersonalizado" 
            value={(form as any).tipoInmueblePersonalizado || ''} 
            onChange={handleChange}
            placeholder="Especifique el tipo de inmueble..."
          />
        )}
      </FormControl>

      <FormControl>
        <FormLabel>Superficie Construida (m²)</FormLabel>
        <Input type="number" name="superficeConstruida" value={form.superficeConstruida?.toString() || ''} onChange={handleChange} />
      </FormControl>
      
      <FormControl>
        <FormLabel>Niveles</FormLabel>
        <Input type="number" name="niveles" value={form.niveles?.toString() || ''} onChange={handleChange} />
      </FormControl>
      
      <FormControl>
        <FormLabel>Denominación del Predio</FormLabel>
        <Input name="predioDenominacion" value={form.predioDenominacion || ''} onChange={handleChange} />
      </FormControl>
    </SimpleGrid>
  </Box>
)}

  <Box mt={4} mb={8} pb={2}>
  <FormLabel>Contenido del Dictamen</FormLabel>
  <ReactQuill
    theme="snow"
    value={form.contenidoDictamen || ''}
    onChange={handleQuillChange}
    style={{ height: '100px', backgroundColor: 'white', marginBottom: '40px' }}
  />
</Box>

  {/* SECCIÓN: Estatus y Pago */}
<Box mt={8} mb={4}>
  <FormLabel fontSize="lg" fontWeight="bold">Información de Entrega y Pago</FormLabel>
  <Divider mb={4} />
  
  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
    <FormControl>
      <FormLabel>Estatus de Entrega</FormLabel>
      <Select name="estatusEntrega" value={form.estatusEntrega || ''} onChange={handleChange}>
        <option value="">Seleccione...</option>
        {estatusEntrega.map(e => (
          <option key={e} value={e}>{e}</option>
        ))}
      </Select>
    </FormControl>

    <FormControl>
      <FormLabel>Estatus de Pago</FormLabel>
      <Select name="estatusPago" value={form.estatusPago || ''} onChange={handleChange}>
        <option value="">Seleccione...</option>
        {estatusPago.map(e => (
          <option key={e} value={e}>{e}</option>
        ))}
      </Select>
    </FormControl>
     <FormControl>
    <FormLabel>Monto de Pago</FormLabel>
    <Input type="number" name="montoPago" value={form.montoPago || ''} onChange={handleChange} placeholder='$ 60.00' />
  </FormControl>

  </SimpleGrid>
</Box>
</ModalBody>



        <ModalFooter>
          <Button onClick={handleGuardar} bg="#5b182b" color="white" _hover={{ bg: "#8a1f35" }} mr={3}>
            Guardar
          </Button>
          <Button onClick={() => {
            setForm({ 
              contenidoDictamen: '',
              estatusEntrega: 'Pendiente de entrega',
              estatusPago: 'No pagado',
              direccion: 'San Juan Totolac, Tlax., México',
              latitud: 19.3256653,
              longitud: -98.2568412
            });
            setTipoDictamen('');
            onClose();
          }}>Cancelar</Button>
        </ModalFooter>
      </ModalContent>
      <ModalInspeccion
  isOpen={modalInspeccionOpen}
  onClose={() => setModalInspeccionOpen(false)}
/>

    </Modal>

    
  );
}
